import * as bootstrap from 'bootstrap';
import modbox from 'bootstrap-modbox/dist/bootstrap-modbox.esm';

try {
    window.bootstrap = bootstrap;
    window.modbox = modbox;
} catch (e) {}

// Rocksolid modal coordinator. Solves the long-standing race condition where
// Livewire dispatches `hide-modals` immediately followed by `show-modal` in the
// same commit (e.g. ConnectorTable.createConnectorToken). Bootstrap's hide() is
// async (300ms animation), so a naive show() called next would stack on top of
// the still-animating modal — leaving the original visible with a dead backdrop
// that ignores clicks. The user had to refresh the page to recover.
//
// Fix: hide_modals() returns a Promise that awaits hidden.bs.modal for every
// open modal AND cleans residual backdrop / body state. show_modal() awaits any
// pending hide BEFORE calling Bootstrap.show(). All consumers go through these
// two functions, so the guarantee is global.

let __pendingHide = null;

// Alpine-managed modals (<x-amodal>) handle their own state. Exclude from
// Bootstrap-centric reconciler — they have .modal + .show hardcoded in template
// and toggle visibility via x-show / display style only.
function is_alpine_modal(m) {
    return m.classList.contains('amodal') || m.closest('.amodal-root') !== null;
}

function count_open_modals() {
    let open = 0;
    document.querySelectorAll('.modal').forEach(m => {
        if (is_alpine_modal(m)) {
            // Alpine modals: count as open if display says so (x-show controls it)
            if (m.style.display === 'block' || (m.style.display === '' && m.classList.contains('show') && getComputedStyle(m).display !== 'none')) open++;
            return;
        }
        if (m.classList.contains('show') || m.style.display === 'block') { open++; return; }
        try {
            const inst = bootstrap.Modal.getInstance(m);
            if (inst && inst._isShown) open++;
        } catch (_) {}
    });
    return open;
}

function audit_orphans() {
    // Removes backdrops with no corresponding open modal (orphans from morph
    // or stacked-modal collisions). Uses Bootstrap _isShown state so we never
    // strip a backdrop that Bootstrap is still wiring up.
    const open = count_open_modals();
    const backdrops = document.querySelectorAll('.modal-backdrop');
    if (backdrops.length > open) {
        for (let i = open; i < backdrops.length; i++) backdrops[i].remove();
    }
    if (open === 0) {
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
    }
}

function force_clean_modal_dom(m) {
    // Bootstrap hide() animation can be interrupted by morph or stacked-modal
    // logic, leaving DOM with .show + display:block while _isShown=false. The
    // user sees a "ghost" modal that ignores all clicks. Force-sync DOM to
    // Bootstrap's internal state.
    if (is_alpine_modal(m)) return; // Alpine modals manage their own state
    const inst = bootstrap.Modal.getInstance(m);
    if (inst && inst._isShown) return; // Bootstrap thinks it IS shown, leave alone
    if (m.classList.contains('show') || m.style.display === 'block') {
        m.classList.remove('show');
        m.style.display = 'none';
        m.setAttribute('aria-hidden', 'true');
        m.removeAttribute('aria-modal');
        m.removeAttribute('role');
    }
}

// Track modals we explicitly tried to hide. Reconcile only acts on these,
// avoiding false-positive cleanup of modals managed entirely by other code.
const __closingModalIds = new Set();

function hide_modal_el(m) {
    if (m && m.id) __closingModalIds.add(m.id);
    return new Promise((resolve) => {
        let settled = false;
        const done = () => {
            if (settled) return;
            settled = true;
            // Belt-and-suspenders: verify Bootstrap actually cleaned DOM. If
            // animation was interrupted, .show / display:block can persist.
            force_clean_modal_dom(m);
            // Schedule reconcile passes to catch ghost re-introduced by morph
            setTimeout(reconcile_ghost_modals, 100);
            setTimeout(reconcile_ghost_modals, 400);
            setTimeout(reconcile_ghost_modals, 1000);
            resolve();
        };
        m.addEventListener('hidden.bs.modal', done, { once: true });
        const inst = bootstrap.Modal.getInstance(m);
        try {
            if (inst && inst._isShown) {
                inst.hide();
            } else {
                // No instance OR already hidden: still clean DOM if dirty
                done();
            }
        } catch (_) {
            done();
        }
        // Safety net: hidden.bs.modal sometimes never fires (morph race).
        setTimeout(done, 600);
    });
}

// Track modals opened via our show-modal event coordinator. The hide-modals
// event closes ONLY these modals, not modals opened via direct Bootstrap
// data-bs-toggle clicks (e.g. report-viewer modals with their own lifecycle).
// Without this filter, a stray hide-modals dispatch (e.g. from wire:poll
// triggered backend method) would close every visible modal on the page.
const __ourOpenedModalIds = new Set();

function hide_modals() {
    // Only target modals we explicitly opened via show-modal event.
    const open = [...__ourOpenedModalIds]
        .map(id => document.getElementById(id))
        .filter(m => m && (m.classList.contains('show') || m.style.display === 'block'));
    // Also catch ghost-state on those same modals
    open.forEach(m => {
        const inst = bootstrap.Modal.getInstance(m);
        if (inst && !inst._isShown) force_clean_modal_dom(m);
    });
    if (open.length === 0) {
        // Defensive cleanup only when no shown modal AND no pending show
        if (!__pendingShow) audit_orphans();
        __pendingHide = null;
        return Promise.resolve();
    }
    __pendingHide = Promise.all(open.map(hide_modal_el)).then(() => {
        // Drop closed modals from our-opened set
        open.forEach(m => __ourOpenedModalIds.delete(m.id));
        __pendingHide = null;
        if (!__pendingShow) audit_orphans();
    });
    return __pendingHide;
}

let __pendingShow = false;

async function show_modal(name) {
    const target = document.getElementById(name + 'Modal');
    if (!target) return;
    __ourOpenedModalIds.add(name + 'Modal');
    __pendingShow = true;
    try {
        // Critical: wait for any pending hide to fully complete before showing
        // — prevents the addConnector → showToken stacked-modal bug.
        if (__pendingHide) {
            try { await __pendingHide; } catch (_) {}
        }
        // No-op if already shown and Bootstrap state healthy. Backend code
        // sometimes re-dispatches show-modal after wire:model commit (every
        // form field change) — dispose+recreate would cause visible flicker.
        const existing = bootstrap.Modal.getInstance(target);
        if (existing && existing._isShown && target.classList.contains('show')) {
            return;
        }
        // Dispose stale instance so Bootstrap.show() doesn't early-return on
        // bad internal state (post hide-cycle, after morph element-replace).
        try { if (existing) existing.dispose(); } catch (_) {}
        target.classList.remove('show');
        target.style.display = '';
        target.removeAttribute('aria-hidden');
        const instance = bootstrap.Modal.getOrCreateInstance(target);
        try { instance.show(); } catch (_) {}
    } finally {
        __pendingShow = false;
    }
}

window.addEventListener('hide-modals', () => { hide_modals(); });
window.addEventListener('show-modal', (event) => {
    const name = event.detail && (event.detail.modal || event.detail.name);
    if (name) show_modal(name);
});

// Track modals that should currently be open. Reconcile uses this to RE-SHOW
// any modal whose DOM element gets replaced by Livewire morph (which kills
// Bootstrap's instance binding). Sources of "expected open":
//   - data-bs-toggle="modal" link click
//   - show-modal event (already added in show_modal())
//   - direct bootstrap.Modal.show() call (caught via shown.bs.modal event)
const __expectedOpenIds = new Set();

document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-bs-toggle="modal"][data-bs-target]');
    if (!trigger) return;
    const target = trigger.getAttribute('data-bs-target');
    if (target && target.startsWith('#')) __expectedOpenIds.add(target.substring(1));
}, true);

document.addEventListener('shown.bs.modal', (e) => {
    if (e.target && e.target.id) __expectedOpenIds.add(e.target.id);
});
document.addEventListener('hide.bs.modal', (e) => {
    // User-initiated close (X button, ESC, backdrop click) → drop from expected
    if (e.target && e.target.id) __expectedOpenIds.delete(e.target.id);
});

// Belt-and-suspenders: after every Bootstrap modal hide, audit orphan backdrops
// — but only when no show is pending (avoid stripping the new modal's backdrop).
document.addEventListener('hidden.bs.modal', () => {
    setTimeout(() => { if (!__pendingShow && !__pendingHide) audit_orphans(); }, 100);
});

// Self-healing watchdog with intent-aware ghost handling:
//
// Ghost = DOM has .show class but Bootstrap state says hidden (_isShown=false
// or no instance). Caused by Livewire 4 morph occasionally REPLACING the
// modal element despite wire:ignore — Bootstrap loses its instance binding.
//
// Two ghost types:
// (A) Modal user EXPECTS open (DOM .show present, never explicitly closed)
//     → RE-ATTACH Bootstrap instance + show() to restore backdrop+state.
// (B) Modal user EXPECTS closed (in __closingModalIds set)
//     → force-clean DOM (remove .show, display:none).
function reconcile_ghost_modals() {
    // (1) Re-show modals that user expects open but element got replaced
    __expectedOpenIds.forEach(id => {
        const m = document.getElementById(id);
        if (!m || is_alpine_modal(m)) return;
        if (__closingModalIds.has(id)) return; // user closing, skip
        let inst = null;
        try { inst = bootstrap.Modal.getInstance(m); } catch (_) {}
        if (inst && inst._isShown) return; // healthy, still shown
        // Element exists but no Bootstrap state → likely replaced by morph.
        // Re-attach Bootstrap and re-show.
        try {
            if (inst) inst.dispose();
            m.classList.remove('show');
            m.style.display = '';
            m.removeAttribute('aria-hidden');
            const newInst = bootstrap.Modal.getOrCreateInstance(m);
            newInst.show();
        } catch (_) {}
    });
    // (2) Ghost cleanup for modals user closed (in __closingModalIds)
    document.querySelectorAll('.modal').forEach(m => {
        if (is_alpine_modal(m)) return;
        const isVisuallyShown = m.classList.contains('show') || m.style.display === 'block';
        if (!isVisuallyShown) return;
        let inst = null;
        try { inst = bootstrap.Modal.getInstance(m); } catch (_) {}
        if (inst && inst._isShown) return;
        if (m.id && __closingModalIds.has(m.id)) {
            force_clean_modal_dom(m);
        }
    });
    [...__closingModalIds].forEach(id => {
        const m = document.getElementById(id);
        if (!m || (!m.classList.contains('show') && m.style.display !== 'block')) {
            __closingModalIds.delete(id);
        }
    });
    // Orphan backdrop cleanup (only when fully idle)
    if (__pendingHide || __pendingShow) return;
    const open = count_open_modals();
    const backdrops = document.querySelectorAll('.modal-backdrop');
    if (backdrops.length > open) {
        for (let i = open; i < backdrops.length; i++) backdrops[i].remove();
    }
    if (open === 0 && document.body.classList.contains('modal-open')) {
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
    }
}

// Backstop interval: cheap orphan cleanup at 1s. Doesn't touch modal elements,
// only backdrop+body when state is confirmed clean.
setInterval(reconcile_ghost_modals, 1000);

// (tracking now inlined in hide_modal_el directly)

document.addEventListener('livewire:init', () => {
    if (window.Livewire && typeof window.Livewire.hook === 'function') {
        try {
            window.Livewire.hook('commit', (payload) => {
                payload.succeed(() => {
                    setTimeout(reconcile_ghost_modals, 100);
                });
            });
        } catch (_) {}
    }
});

// Expose for debugging.
try {
    window.__closingModalIds = __closingModalIds;
    window.__ourOpenedModalIds = __ourOpenedModalIds;
    window.__hideModals = hide_modals;
    window.__showModal = show_modal;
    window.__auditOrphans = audit_orphans;
    window.__reconcile = reconcile_ghost_modals;
} catch (_) {}

function confirm_modal(message, confirm) {
    return modbox.confirm({ body: message, okButton: { label: confirm } });
}

var topToggle = document.getElementById('sidebarToggleTop');
var downToggle = document.getElementById('sidebarToggle');

function toggleSidebar() {
    document.querySelector("body").classList.toggle("sidebar-toggled");
    document.querySelector(".sidebar").classList.toggle("toggled");
}
if (topToggle) {
    topToggle.addEventListener('click', (e) => { e.preventDefault(); toggleSidebar(); });
}
if (downToggle) {
    downToggle.addEventListener('click', (e) => { e.preventDefault(); toggleSidebar(); });
}
